import { ApiError, fetchMe, meQueryKey, type WebModuleDefinition } from '@portal/web-sdk';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type ParsedLocation,
} from '@tanstack/react-router';
import { AppShell } from './shell/AppShell';
import { BlankLayout } from './shell/BlankLayout';
import { NotFound, RouteError } from './shell/RouteStates';

export interface RouterContext {
  queryClient: QueryClient;
}

/** Where a restricted account is sent (pages that every restricted user may open). */
const RESTRICTION_HOME: Record<string, string> = {
  'core-identity.account_pending': '/pending',
  'core-identity.two_factor_required': '/account',
};

/**
 * Loads the current user before a page renders. Not signed in → /login (back here afterwards);
 * signed in but restricted → the page that explains what to do next.
 */
async function requireSession(
  context: RouterContext,
  location: ParsedLocation,
  allowRestricted: boolean,
) {
  let me;
  try {
    me = await context.queryClient.ensureQueryData({ queryKey: meQueryKey, queryFn: fetchMe });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      throw redirect({ to: '/login', search: { redirect: location.href } as never });
    }
    throw error;
  }
  if (me.restriction && !allowRestricted) {
    throw redirect({ to: RESTRICTION_HOME[me.restriction] ?? '/pending' });
  }
}

/**
 * Routes are code-based (not file-based) because they come from module definitions.
 * Each module page is lazy-loaded, so a module ships in its own chunk.
 */
export function buildRouter(modules: WebModuleDefinition[], queryClient: QueryClient) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: Outlet,
    notFoundComponent: NotFound,
    errorComponent: RouteError,
  });
  const appLayout = createRoute({
    getParentRoute: () => rootRoute,
    id: 'app',
    component: AppShell,
    notFoundComponent: NotFound,
  });
  const blankLayout = createRoute({
    getParentRoute: () => rootRoute,
    id: 'blank',
    component: BlankLayout,
  });

  const seen = new Map<string, string>();
  const moduleRoutes = modules.flatMap(({ manifest, routes }) =>
    routes.map((route) => {
      const owner = seen.get(route.path);
      if (owner) {
        throw new Error(`Route "${route.path}" is declared by both ${owner} and ${manifest.key}`);
      }
      seen.set(route.path, manifest.key);
      const layout = route.layout === 'blank' ? blankLayout : appLayout;
      const created = createRoute({
        getParentRoute: () => layout,
        path: route.path,
        component: lazyRouteComponent(route.component),
        beforeLoad: route.public
          ? undefined
          : ({ context, location }) =>
              requireSession(context, location, route.allowRestricted ?? false),
      });
      return { layout, route: created };
    }),
  );
  // `route.parentRoute` is only set once the tree is built, so remember each route's layout here.
  const under = (layout: typeof appLayout | typeof blankLayout) =>
    moduleRoutes.filter((r) => r.layout === layout).map((r) => r.route);

  return createRouter({
    routeTree: rootRoute.addChildren([
      appLayout.addChildren(under(appLayout)),
      blankLayout.addChildren(under(blankLayout)),
    ]),
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}
